import { Waypoint_Update_Destroyable, WaypointType } from '@treadinc/horizon-api-spec';

import { ContactItem } from '~hooks/useContact/models/ContactItem';
import { Site, SiteBasic, WayPoint, WayPointFormProps } from '~hooks/useSites/models';
import { Nullable } from '~types/Nullable';

interface LoadWaypointFormSites {
  pickUp: Nullable<Site | SiteBasic>;
  dropOff: Nullable<Site | SiteBasic>;
  staging: Nullable<Site | SiteBasic>;
}

interface LoadWaypointDirtyFlags {
  pickUp: boolean;
  dropOff: boolean;
  staging: boolean;
}

const PICKUP_ORDINALITY = 10;
const STAGING_ORDINALITY = 15;
const DROPOFF_ORDINALITY = 20;

function buildEntry(
  type: WaypointType,
  ordinality: number,
  site: Nullable<Site | SiteBasic>,
  isDirty: boolean,
  originalWaypoints: WayPoint[],
): Nullable<Waypoint_Update_Destroyable> {
  if (!isDirty) return null;

  const original = originalWaypoints.find((wp) => wp.type === type);

  if (site?.id) {
    return WayPoint.deparse(
      {
        id: original?.id || '',
        site,
        ordinality,
        type,
        contact: (original?.contact ?? undefined) as ContactItem,
      } as WayPointFormProps,
      false,
    );
  }

  // User cleared the selection. If a waypoint already existed, mark it for destruction.
  if (original?.id) {
    return { id: original.id, _destroy: 1 } as Waypoint_Update_Destroyable;
  }

  return null;
}

export function buildLoadWaypointsUpdate(
  originalWaypoints: WayPoint[],
  formSites: LoadWaypointFormSites,
  dirty: LoadWaypointDirtyFlags,
): Waypoint_Update_Destroyable[] | undefined {
  const entries = [
    buildEntry(
      WaypointType.PICKUP,
      PICKUP_ORDINALITY,
      formSites.pickUp,
      dirty.pickUp,
      originalWaypoints,
    ),
    buildEntry(
      WaypointType.STAGING,
      STAGING_ORDINALITY,
      formSites.staging,
      dirty.staging,
      originalWaypoints,
    ),
    buildEntry(
      WaypointType.DROP_OFF,
      DROPOFF_ORDINALITY,
      formSites.dropOff,
      dirty.dropOff,
      originalWaypoints,
    ),
  ].filter((e): e is Waypoint_Update_Destroyable => e !== null);

  return entries.length ? entries : undefined;
}
